import {Alert, FormControl} from 'platform/components';
import {UseDocumentPreviewReturnType} from 'platform/document-preview';
import {Heading, HStack, Icon, VStack, Text, Show, Hide} from 'platform/foundation';
import {match} from 'ts-pattern';

import {UseFormReturn, useWatch} from 'react-hook-form';

import i18n from '@dms/i18n';

import {DOT_CHARACTER, useDebouncedValue} from 'shared/web';

import {DelayEnum} from '../constants/DelayEnum';
import {useDocumentExtractionBehaviour} from '../hooks/useDocumentExtractionBehaviour';
import {useInvalidItemsCount} from '../hooks/useInvalidItemsCount';
import {DeliveryNoteDocumentPreviewFormDefinition} from '../types/DeliveryNoteDocumentPreviewFormDefinition';

interface ListOfItemsHeaderProps {
  isConfirmed: boolean;
  isLoadingSuggestions: boolean;
  formApi: UseFormReturn<DeliveryNoteDocumentPreviewFormDefinition>;
  control: FormControl<DeliveryNoteDocumentPreviewFormDefinition>;
  pageNumber: number;
  hasMoreThanOnePage: boolean;
  documentPreview: UseDocumentPreviewReturnType;
}

export function ListOfItemsHeader(props: ListOfItemsHeaderProps) {
  const listOfItems = useWatch({control: props.control, name: 'listOfItems'});

  const debouncedListOfItems = useDebouncedValue(listOfItems, DelayEnum.HEADER);

  const behaviour = useDocumentExtractionBehaviour();

  const {
    invalidItemsCount,
    invalidItemsCountPerPage,
    priceMismatchesCount,
    priceMismatchesCountPerPage,
  } = useInvalidItemsCount({
    allowZeroPrices: behaviour.allowZeroPrices,
    isPriceRecalculationEnabled: behaviour.isPriceRecalculationEnabled,
    isConfirmed: props.isConfirmed,
    pageNumber: props.pageNumber,
    listOfItems: debouncedListOfItems,
    documentPreview: props.documentPreview,
  });

  const hasActiveItems = debouncedListOfItems.some((item) => item.isActive);

  const hasInvalidItems = invalidItemsCount + priceMismatchesCount > 0;

  const indicatorIcon = match({invalidItemsCount, priceMismatchesCount})
    .when(
      ({invalidItemsCount}) => invalidItemsCount > 0,
      () => ({value: 'alert/error' as const, color: 'severity.danger' as const})
    )
    .when(
      ({priceMismatchesCount}) => priceMismatchesCount > 0,
      () => ({value: 'alert/warning' as const, color: 'severity.warning' as const})
    )
    .otherwise(() => ({value: 'action/check_circle' as const, color: 'severity.success' as const}));

  const hasInvalidItemsOnCurrentPage = invalidItemsCountPerPage > 0;
  const hasPriceMismatchesOnCurrentPage = priceMismatchesCountPerPage > 0;

  return (
    <VStack spacing={4}>
      <HStack justify="space-between">
        <Heading size={4}>{i18n.t('entity.warehouse.labels.listOfItems')}</Heading>

        <Hide when={!props.isConfirmed}>
          <Hide when={!hasActiveItems}>
            <HStack spacing={1}>
              <HStack spacing={1}>
                <Icon value={indicatorIcon.value} color={indicatorIcon.color} size={4} />
                <Text size="xSmall">{i18n.t('entity.documentExtraction.labels.allPages')}</Text>
                <Text size="xSmall" color="tertiary">
                  {DOT_CHARACTER}
                </Text>
              </HStack>
              <Text size="xSmall">
                {i18n.t('entity.documentExtraction.labels.issues', {
                  count: invalidItemsCount + priceMismatchesCount,
                })}
              </Text>
            </HStack>
          </Hide>
        </Hide>
      </HStack>

      <Show when={props.isLoadingSuggestions}>
        <Alert
          isLoading
          layout="detailed"
          type="inline"
          variant="info"
          title={i18n.t('entity.documentExtraction.labels.checkingArticleCards')}
        />
      </Show>

      <Hide when={props.isLoadingSuggestions}>
        <Show when={!props.isConfirmed}>
          <Alert
            type="inline"
            variant="info"
            title={i18n.t('entity.documentExtraction.labels.itemsAreNotAvailable')}
            message={i18n.t('entity.documentExtraction.labels.itemsAreNotAvailableMessage')}
          />
        </Show>

        <Show when={props.isConfirmed && hasActiveItems && hasInvalidItemsOnCurrentPage}>
          <Alert
            type="inline"
            variant="error"
            title={i18n.t('entity.documentExtraction.labels.invalidPageAlert', {
              pageNumber: props.pageNumber,
              count: invalidItemsCountPerPage,
            })}
          />
        </Show>

        <Show when={props.isConfirmed && hasActiveItems && hasPriceMismatchesOnCurrentPage}>
          <Alert
            type="inline"
            variant="warning"
            title={i18n.t('entity.documentExtraction.labels.pageIssuesAlert', {
              pageNumber: props.pageNumber,
              count: priceMismatchesCountPerPage,
            })}
          />
        </Show>

        <Show
          when={
            props.isConfirmed &&
            hasActiveItems &&
            !hasInvalidItemsOnCurrentPage &&
            !hasPriceMismatchesOnCurrentPage
          }
        >
          <Alert
            type="inline"
            variant="success"
            title={i18n.t('entity.documentExtraction.labels.successPageAlert', {
              pageNumber: props.pageNumber,
            })}
            message={
              props.hasMoreThanOnePage && hasInvalidItems
                ? i18n.t('entity.documentExtraction.labels.successPageAlertMessage')
                : undefined
            }
          />
        </Show>

        <Show when={!hasActiveItems}>
          <Alert
            type="inline"
            variant="warning"
            title={i18n.t('entity.documentExtraction.labels.noItemsActiveAlert')}
            message={i18n.t('entity.documentExtraction.labels.noItemsActiveAlertMessage')}
          />
        </Show>
      </Hide>
    </VStack>
  );
}
